import { useCommunityContext } from 'providers';
import { useQuery } from 'react-query';

import { MarketplaceV1 } from '@jebel/constants';

import { UserKeyFilter } from 'shared/graphql';
import { useHasSession } from 'shared/hooks';

export const USER_PRODUCT_MEMBERSHIPS_QUERY_KEY = 'USER_PRODUCT_MEMBERSHIPS';

/**
 * React hook to fetch a specific user's memberships for the selected community.
 *
 * Uses React Query to asynchronously retrieve membership data from the marketplace API.
 * The query is enabled only if there is an active session and a selected community.
 *
 * @param {UserKeyFilter} user - The user whose memberships should be fetched.
 * @returns The query result containing the membership data.
 * @throws {Error} If the user or selected community is not available, or if the fetch fails.
 */
export function useUserProductMemberships(user: UserKeyFilter | null | undefined) {
  const hasSession = useHasSession();

  const { selectedCommunity } = useCommunityContext();

  return useQuery({
    enabled: Boolean(hasSession && selectedCommunity),
    queryKey: [USER_PRODUCT_MEMBERSHIPS_QUERY_KEY, selectedCommunity?.id, user?.id],

    async queryFn() {
      if (!user?.id) {
        throw new Error('No current user');
      }

      if (!selectedCommunity?.id) {
        throw new Error('No selected community');
      }

      const url = new URL(
        `/community/${selectedCommunity.id}/user/${user.id}/membership`,
        process.env.REACT_APP_MARKETPLACE_API_URL,
      );

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch user memberships (HTTP ${response.status})`);
      }

      const data = await response.json();
      return data as MarketplaceV1.UserCommunityMembership[];
    },
  });
}
